import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Truck, FileText, IndianRupee, Clock, Building2, Loader2, X, Download } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../../admin/AdminLayout";
import AdminAuthGuard from "../../admin/AdminAuthGuard";
import { api, getToken } from "../../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_TONE = {
    submitted: "bg-blue-100 text-blue-700",
    approved: "bg-emerald-100 text-emerald-700",
    processing: "bg-amber-100 text-amber-800",
    dispatched: "bg-violet-100 text-violet-700",
    delivered: "bg-emerald-200 text-emerald-800",
    invoiced: "bg-brand-100 text-brand-800",
    paid: "bg-emerald-300 text-emerald-900",
    closed: "bg-zinc-200 text-zinc-700",
    cancelled: "bg-rose-100 text-rose-700",
};

const Content = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [dispatchOpen, setDispatchOpen] = useState(false);
    const [dispatchForm, setDispatchForm] = useState({ courier: "", awb: "", notes: "" });
    const [payOpen, setPayOpen] = useState(false);
    const [payForm, setPayForm] = useState({ reference: "" });

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/b2b/orders/${id}`);
            setOrder(data);
            if (data.invoice_id) {
                try {
                    const { data: inv } = await api.get(`/admin/b2b/invoices`, { params: { account_id: data.b2b_account_id, page_size: 100 } });
                    setInvoice(inv.items.find((i) => i.id === data.invoice_id) || null);
                } catch { /* ignore */ }
            }
        } catch {
            toast.error("Order not found");
            navigate("/admin/b2b/orders");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const callAction = async (path, body = {}, msg = "Done") => {
        setBusy(true);
        try {
            await api.post(`/admin/b2b/orders/${id}/${path}`, body);
            toast.success(msg);
            await load();
            return true;
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || d?.code || "Action failed");
            return false;
        } finally {
            setBusy(false);
        }
    };

    const dispatchSubmit = async () => {
        if (await callAction("dispatch", dispatchForm, "Order dispatched")) {
            setDispatchOpen(false);
            setDispatchForm({ courier: "", awb: "", notes: "" });
        }
    };

    const markPaid = async () => {
        if (!invoice) return;
        if (!payForm.reference?.trim()) { toast.error("Reference required"); return; }
        setBusy(true);
        try {
            await api.post(`/admin/b2b/invoices/${invoice.id}/mark-paid`, payForm);
            toast.success("Invoice marked paid");
            setPayOpen(false);
            setPayForm({ reference: "" });
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Failed");
        } finally {
            setBusy(false);
        }
    };

    const downloadInvoice = async () => {
        if (!invoice) return;
        try {
            const res = await fetch(`${API_BASE}/api/b2b/invoices/${invoice.id}/pdf`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error("err");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${invoice.invoice_no}.pdf`; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 500);
        } catch {
            toast.error("Could not download PDF");
        }
    };

    if (loading || !order) return <div className="h-[60vh] skeleton rounded-2xl" />;

    return (
        <div className="space-y-5" data-testid="admin-b2b-order-detail">
            <div className="flex items-center justify-between">
                <Link to="/admin/b2b/orders" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-700">
                    <ArrowLeft className="h-4 w-4" /> All B2B orders
                </Link>
                <span className={`rounded-full px-3 py-1 text-[10px] font-semibold uppercase ${STATUS_TONE[order.status]}`}>{order.status.replace("_", " ")}</span>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
                <div className="space-y-5">
                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                                <p className="label-uppercase">B2B Order</p>
                                <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-900" data-testid="admin-b2b-order-no">{order.order_no}</h2>
                                <p className="text-xs text-slate-500">Submitted {new Date(order.created_at).toLocaleString()}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-2xl font-semibold tabular-nums">{inr(order.amounts?.total)}</p>
                                <p className="text-[10px] uppercase tracking-wider text-slate-400">{(order.payment_terms || "").replace("_", " ")}</p>
                            </div>
                        </div>
                        <ul className="mt-5 divide-y divide-slate-100">
                            {(order.items || []).map((it, i) => (
                                <li key={i} className="flex items-center gap-3 py-3">
                                    {it.image && <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover ring-1 ring-slate-200" />}
                                    <div className="min-w-0 flex-1">
                                        <p className="line-clamp-1 text-sm font-medium text-slate-900">{it.name}</p>
                                        <p className="text-xs text-slate-500">{it.brand} · {it.pack_size} · qty {it.qty} · GST {it.gst_percent}%</p>
                                        <p className="text-[11px] text-slate-500">{inr(it.unit_price)} × {it.qty} (MRP {inr(it.mrp || 0)})</p>
                                    </div>
                                    <p className="text-sm font-medium tabular-nums">{inr(it.subtotal)}</p>
                                </li>
                            ))}
                        </ul>
                    </section>

                    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="admin-b2b-order-history">
                        <p className="label-uppercase">Activity timeline</p>
                        <ol className="mt-3 space-y-2.5">
                            {(order.history || []).map((h, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-xs">
                                    <Clock className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                                    <div>
                                        <p className="font-medium capitalize text-slate-900">{h.event.replace("_", " ")}</p>
                                        {h.note && <p className="text-slate-500">{h.note}</p>}
                                        <p className="text-[10px] uppercase tracking-wider text-slate-400">{new Date(h.at).toLocaleString()}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </section>
                </div>

                <aside className="space-y-5">
                    {order.account && (
                        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                            <p className="label-uppercase">Buyer</p>
                            <Building2 className="mt-3 h-4 w-4 text-slate-400" />
                            <p className="mt-1 text-sm font-medium">{order.account.legal_name}</p>
                            <p className="text-xs text-slate-500">{order.account.gstin}</p>
                            <p className="text-xs text-slate-500">{order.account.primary_contact_name} · {order.account.phone}</p>
                            {order.account.address && (
                                <p className="mt-2 text-xs text-slate-600">{order.account.address.line1}, {order.account.address.city} - {order.account.address.pincode}</p>
                            )}
                            <Link to="/admin/b2b/accounts" className="mt-2 inline-block text-[11px] font-medium text-brand-700 hover:underline">All accounts →</Link>
                        </div>
                    )}

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft">
                        <p className="label-uppercase">Breakdown</p>
                        <dl className="mt-3 space-y-1.5 text-sm">
                            <div className="flex justify-between"><dt className="text-slate-500">Subtotal</dt><dd className="tabular-nums">{inr(order.amounts?.subtotal)}</dd></div>
                            {order.amounts?.discount > 0 && (
                                <div className="flex justify-between text-emerald-700"><dt>Wholesale discount</dt><dd className="tabular-nums">−{inr(order.amounts.discount)}</dd></div>
                            )}
                            <div className="flex justify-between"><dt className="text-slate-500">GST</dt><dd className="tabular-nums">{inr(order.amounts?.gst_amount)}</dd></div>
                            <div className="flex justify-between border-t border-slate-100 pt-2 font-semibold"><dt>Total</dt><dd className="tabular-nums">{inr(order.amounts?.total)}</dd></div>
                        </dl>
                    </div>

                    {invoice && (
                        <div className="rounded-2xl border border-brand-200 bg-brand-50/40 p-5">
                            <p className="label-uppercase">Invoice</p>
                            <p className="mt-1 font-mono text-sm font-semibold">{invoice.invoice_no}</p>
                            <p className="text-xs text-slate-600">Due {new Date(invoice.due_date).toLocaleDateString()} · Status {invoice.status}</p>
                            <div className="mt-3 flex gap-2">
                                <button onClick={downloadInvoice} className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-xs hover:bg-zinc-50" data-testid="admin-b2b-order-invoice-download">
                                    <Download className="h-3 w-3" /> PDF
                                </button>
                                {invoice.status !== "paid" && (
                                    <button onClick={() => setPayOpen(true)} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-emerald-700" data-testid="admin-b2b-order-mark-paid">
                                        <IndianRupee className="h-3 w-3" /> Mark paid
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-soft" data-testid="admin-b2b-order-actions">
                        <p className="label-uppercase">Actions</p>
                        <div className="mt-3 flex flex-col gap-2">
                            {order.status === "submitted" && (
                                <button onClick={() => callAction("approve", {}, "Order approved")} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="admin-b2b-order-approve">
                                    <CheckCircle2 className="h-4 w-4" /> Approve order
                                </button>
                            )}
                            {["approved", "processing"].includes(order.status) && (
                                <button onClick={() => setDispatchOpen(true)} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50" data-testid="admin-b2b-order-dispatch">
                                    <Truck className="h-4 w-4" /> Dispatch
                                </button>
                            )}
                            {order.status === "dispatched" && (
                                <button onClick={() => callAction("mark-delivered", {}, "Marked delivered")} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="admin-b2b-order-delivered">
                                    <CheckCircle2 className="h-4 w-4" /> Mark delivered
                                </button>
                            )}
                            {!order.invoice_id && ["delivered", "dispatched", "approved", "processing"].includes(order.status) && (
                                <button onClick={() => callAction("invoice", {}, "Invoice issued")} disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid="admin-b2b-order-invoice">
                                    <FileText className="h-4 w-4" /> Generate invoice
                                </button>
                            )}
                            {order.dispatch?.awb && (
                                <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-xs">
                                    <p className="font-medium text-slate-900">AWB: {order.dispatch.awb}</p>
                                    <p className="text-slate-500">{order.dispatch.courier}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </aside>
            </div>

            {dispatchOpen && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="admin-b2b-dispatch-modal">
                        <div className="flex items-center justify-between">
                            <p className="label-uppercase">Dispatch order</p>
                            <button onClick={() => setDispatchOpen(false)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-3.5 w-3.5" /></button>
                        </div>
                        <h2 className="text-lg font-semibold tracking-tight">Manual dispatch</h2>
                        <p className="mt-1 text-xs text-slate-500">Enter courier + AWB details. Buyer will be notified.</p>
                        <div className="mt-4 space-y-3">
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Courier</span>
                                <input value={dispatchForm.courier} onChange={(e) => setDispatchForm({ ...dispatchForm, courier: e.target.value })} placeholder="Delhivery, Bluedart, …" className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5 text-sm" data-testid="admin-b2b-dispatch-courier" />
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">AWB number</span>
                                <input value={dispatchForm.awb} onChange={(e) => setDispatchForm({ ...dispatchForm, awb: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5 text-sm" data-testid="admin-b2b-dispatch-awb" />
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Notes (optional)</span>
                                <textarea rows={2} value={dispatchForm.notes} onChange={(e) => setDispatchForm({ ...dispatchForm, notes: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 p-2 text-xs" />
                            </label>
                        </div>
                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setDispatchOpen(false)} className="flex-1 rounded-lg border border-zinc-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={dispatchSubmit} disabled={busy} className="flex-1 rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50" data-testid="admin-b2b-dispatch-submit">
                                {busy ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" /> : "Confirm dispatch"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {payOpen && invoice && (
                <div className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/40 p-4 backdrop-blur-sm">
                    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" data-testid="admin-b2b-mark-paid-modal">
                        <div className="flex items-center justify-between">
                            <p className="label-uppercase">Mark invoice paid</p>
                            <button onClick={() => setPayOpen(false)} className="rounded-full p-1.5 text-zinc-400 hover:bg-zinc-100"><X className="h-3.5 w-3.5" /></button>
                        </div>
                        <h2 className="text-lg font-semibold tracking-tight">Invoice {invoice.invoice_no}</h2>
                        <p className="mt-1 text-xs text-slate-500">Amount: <strong>{inr(invoice.amount)}</strong></p>
                        <label className="mt-4 block">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Payment reference (UTR / cheque #)</span>
                            <input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="mt-1 w-full rounded-md border border-zinc-200 px-2 py-1.5 text-sm" data-testid="admin-b2b-mark-paid-ref" />
                        </label>
                        <div className="mt-5 flex gap-2">
                            <button onClick={() => setPayOpen(false)} className="flex-1 rounded-lg border border-zinc-200 px-4 py-2 text-sm">Cancel</button>
                            <button onClick={markPaid} disabled={busy} className="flex-1 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50" data-testid="admin-b2b-mark-paid-submit">
                                {busy ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" /> : "Mark paid"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const AdminB2BOrderDetail = () => (
    <AdminAuthGuard>
        <AdminLayout title="B2B order" subtitle="Approve, dispatch, invoice">
            <Content />
        </AdminLayout>
    </AdminAuthGuard>
);

export default AdminB2BOrderDetail;
